/**
 * Shared utilities for condition preview components.
 */

import type { RelatedLogEntry } from './constants';

// =============================================================================
// Related Log Lookup
// =============================================================================

/**
 * Find an EventType entry in related logs by ID.
 * Returns the EventType data if found, null otherwise.
 * Checks both `data` and `changed_to` fields as the backend may use either.
 */
export function findEventTypeInRelatedLogs(
  eventTypeId: number,
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> | null {
  if (relatedLogs == null || eventTypeId <= 0) return null;

  const eventTypeLog = relatedLogs.find(
    (log) => log.object_type === 'EventType' && log.object_id === String(eventTypeId),
  );

  if (eventTypeLog == null) return null;

  // Prefer `data`, fall back to `changed_to`
  const data = eventTypeLog.data as Record<string, unknown> | undefined;
  if (data != null && typeof data === 'object' && Object.keys(data).length > 0) {
    return data;
  }

  const changedTo = eventTypeLog.changed_to as Record<string, unknown> | undefined;
  if (changedTo != null && typeof changedTo === 'object' && Object.keys(changedTo).length > 0) {
    return changedTo;
  }

  return null;
}

/**
 * Enrich condition data with event type information from related logs.
 * For event conditions (type: 1), looks up the EventType to get the event name
 * and any filters (event attributes) stored with the EventType.
 */
export function enrichConditionWithEventType(
  conditionData: Record<string, unknown>,
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> {
  // Check if this is an event condition with event_type_id
  const eventTypeId = conditionData.event_type_id as number | undefined;
  if (eventTypeId == null || eventTypeId <= 0) {
    return conditionData;
  }

  // Look up the EventType in related logs
  const eventType = findEventTypeInRelatedLogs(eventTypeId, relatedLogs);
  if (eventType == null) {
    return conditionData;
  }

  // Extract event name and type from EventType data
  const eventName = eventType.name as string | undefined;
  const eventTypeName = eventType.type as string | undefined; // e.g., "event", "delivered_email", "screen"

  // Extract filters from EventType - the backend stores event attribute filters here
  // Filter structure: { attribute: { field: "turtle", operator: "eq", value: "true" } }
  // or { and: [...] } or { or: [...] }
  const eventTypeFilter = eventType.filter as Record<string, unknown> | undefined;

  // Build event object with all available data
  const eventObject: Record<string, unknown> = {
    name: eventName,
    type: eventTypeName,
    ui_type: eventType.ui_type as string | undefined,
  };

  // Add filters from EventType if present
  // Convert the EventType filter format to the format our parsers expect
  if (eventTypeFilter != null && Object.keys(eventTypeFilter).length > 0) {
    // The EventType stores filters as { attribute: {...} } or { and: [...] } or { or: [...] }
    if ('attribute' in eventTypeFilter) {
      // Single filter: { attribute: { field, operator, value } }
      eventObject.filters = { and: [eventTypeFilter.attribute] };
    } else if ('and' in eventTypeFilter || 'or' in eventTypeFilter) {
      // Group filter: { and: [...] } or { or: [...] }
      eventObject.filters = eventTypeFilter;
    } else {
      // Direct filter object
      eventObject.filters = { and: [eventTypeFilter] };
    }
  }

  // Enrich the condition data with event information
  return {
    ...conditionData,
    // Add event object with name, type, and filters for parseEventCondition to use
    event: eventObject,
    // Also set event_name directly as a fallback
    event_name: eventName,
  };
}

/**
 * Find Segment data in related logs by segment_id.
 */
export function findSegmentInRelatedLogs(
  segmentId: number,
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> | null {
  if (relatedLogs == null || relatedLogs.length === 0) return null;

  for (const log of relatedLogs) {
    if (log.object_type === 'Segment' && log.object_id === String(segmentId)) {
      // Prefer data, fall back to changed_to
      const data = (log.data ?? log.changed_to) as Record<string, unknown> | undefined;
      if (data != null && Object.keys(data).length > 0) {
        return data;
      }
    }
  }

  return null;
}

/**
 * Generic helper to enrich condition data with a name from an ID lookup.
 * This reduces duplication across enrichment functions that follow the pattern:
 * 1. Extract ID from condition data
 * 2. Look up name from data source
 * 3. Add name to condition data
 *
 * @param conditionData - The condition data to enrich
 * @param options - Configuration for the enrichment
 * @returns Enriched condition data with the name field added if found
 */
function enrichConditionWithName(
  conditionData: Record<string, unknown>,
  options: {
    nameField: string;
    idExtractor: (data: Record<string, unknown>) => number | undefined;
    nameLookup: (id: number) => string | undefined;
    idValidator?: (id: number | undefined) => boolean;
  },
): Record<string, unknown> {
  const { nameField, idExtractor, nameLookup, idValidator } = options;

  const id = idExtractor(conditionData);
  if (id == null) {
    return conditionData;
  }

  // Use custom validator if provided, otherwise default check
  if (idValidator != null && !idValidator(id)) {
    return conditionData;
  }
  if (idValidator == null && id <= 0) {
    return conditionData;
  }

  const name = nameLookup(id);
  if (name == null) {
    return conditionData;
  }

  return {
    ...conditionData,
    [nameField]: name,
  };
}

/**
 * Campaign data structure for looking up campaign names.
 */
export interface CampaignInfo {
  id: number;
  name: string;
}

/**
 * Find campaign name from campaigns list by ID.
 * Uses Number() comparison to handle string/number type mismatches.
 */
export function findCampaignName(campaignId: number, campaigns?: CampaignInfo[]): string | undefined {
  if (campaigns == null || campaigns.length === 0) return undefined;
  const campaign = campaigns.find((c) => Number(c.id) === campaignId);
  return campaign?.name;
}

/**
 * Extract campaign ID from message name patterns like "email-campaign_2".
 * Returns the numeric campaign ID or undefined if not a campaign pattern.
 */
export function extractCampaignIdFromName(messageName: string | undefined): number | undefined {
  if (messageName == null) return undefined;

  // Match patterns like "email-campaign_2", "twilio-campaign_15", etc.
  const match = messageName.match(/-campaign_(\d+)$/);
  if (match != null) {
    return Number.parseInt(match[1], 10);
  }
  return undefined;
}

/**
 * Enrich condition data with campaign name from campaigns list.
 * This looks up the campaign name based on:
 * 1. campaign_id field in event object or condition data
 * 2. Message name patterns like "email-campaign_2"
 */
export function enrichConditionWithCampaignName(
  conditionData: Record<string, unknown>,
  campaigns?: CampaignInfo[],
): Record<string, unknown> {
  if (campaigns == null || campaigns.length === 0) {
    return conditionData;
  }

  // Check for event object with name containing campaign pattern
  const event = conditionData.event as Record<string, unknown> | undefined;
  const eventName = event?.name as string | undefined;

  // Also check direct name field
  const directName = conditionData.name as string | undefined;
  const messageName = eventName ?? directName;

  // First, try to get campaign_id from event object or condition data
  let campaignId: number | undefined;
  if (event != null && 'campaign_id' in event) {
    const id = event.campaign_id;
    campaignId = typeof id === 'number' ? id : typeof id === 'string' ? Number.parseInt(id, 10) : undefined;
  } else if ('campaign_id' in conditionData) {
    const id = conditionData.campaign_id;
    campaignId = typeof id === 'number' ? id : typeof id === 'string' ? Number.parseInt(id, 10) : undefined;
  }

  // If no campaign_id found, try extracting from message name pattern
  if (campaignId == null) {
    campaignId = extractCampaignIdFromName(messageName);
  }

  if (campaignId == null) {
    return conditionData;
  }

  const campaignName = findCampaignName(campaignId, campaigns);
  if (campaignName == null) {
    return conditionData;
  }

  // Add campaign name to the event object if present, otherwise to the top level
  if (event != null) {
    return {
      ...conditionData,
      event: {
        ...event,
        campaign_name: campaignName,
      },
    };
  }

  return {
    ...conditionData,
    campaign_name: campaignName,
  };
}

/**
 * Enrich condition data with Segment name from related logs.
 * This is used to display the actual segment name instead of "Segment #123".
 */
export function enrichConditionWithSegmentName(
  conditionData: Record<string, unknown>,
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> {
  return enrichConditionWithName(conditionData, {
    nameField: 'segment_name',
    idExtractor: (data) => data.segment_id as number | undefined,
    nameLookup: (segmentId) => {
      const segmentData = findSegmentInRelatedLogs(segmentId, relatedLogs);
      return segmentData?.name as string | undefined;
    },
    idValidator: (id) => id != null && id > 0,
  });
}

/**
 * Find CampaignAction data in related logs by output_id (message name like "output_5").
 * Returns the CampaignAction data if found, null otherwise.
 */
export function findMessageInRelatedLogs(
  outputId: string,
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> | null {
  if (relatedLogs == null || relatedLogs.length === 0 || outputId == null || outputId === '') {
    return null;
  }

  for (const log of relatedLogs) {
    if (log.object_type === 'CampaignAction') {
      const data = (log.data ?? log.changed_to) as Record<string, unknown> | undefined;
      if (data != null && typeof data === 'object') {
        // Check if this action has a matching output_id or name
        const actionOutputId = data.output_id as string | undefined;
        const actionName = data.name as string | undefined;
        if (actionOutputId === outputId || actionName === outputId) {
          return data;
        }
      }
    }
  }

  return null;
}

/**
 * Message data structure for looking up message names.
 * Messages have an output_id (like "output_5") and a subject/name for display.
 */
export interface MessageInfo {
  output_id: string;
  subject?: string;
  name?: string;
  type?: string;
  campaign_id?: number;
}

/**
 * Enrich condition data with message name from messages list or related logs.
 * This looks up campaign actions by output_id (like "output_5") and uses the action's name/subject.
 * Also adds campaign_id from the message if available, which can be used for campaign name enrichment.
 * Priority: 1) messages list, 2) related logs
 */
export function enrichConditionWithMessageName(
  conditionData: Record<string, unknown>,
  messages?: MessageInfo[],
  relatedLogs?: RelatedLogEntry[],
): Record<string, unknown> {
  // Extract message name from event.name or condition name
  const event = conditionData.event as Record<string, unknown> | undefined;
  const eventName = event?.name as string | undefined;
  const directName = conditionData.name as string | undefined;
  const messageName = eventName ?? directName;

  // Skip if it's a wildcard or campaign pattern
  if (
    messageName == null ||
    messageName === '' ||
    messageName === '*' ||
    messageName === '*_h' ||
    messageName.endsWith('_*') ||
    messageName.includes('-campaign_')
  ) {
    return conditionData;
  }

  let messageDisplayName: string | undefined;
  let messageCampaignId: number | undefined;

  // First, try to find in messages list (if provided)
  if (messages != null && messages.length > 0) {
    const message = messages.find((m) => m.output_id === messageName);
    if (message != null) {
      messageDisplayName = message.subject ?? message.name;
      messageCampaignId = message.campaign_id;
    }
  }

  // Fallback: look up in related logs
  if (messageDisplayName == null && relatedLogs != null && relatedLogs.length > 0) {
    const messageData = findMessageInRelatedLogs(messageName, relatedLogs);
    if (messageData != null) {
      messageDisplayName = (messageData.subject ?? messageData.name) as string | undefined;
      // Try to extract campaign_id from related log data
      const dataCampaignId = messageData.campaign_id;
      if (dataCampaignId != null) {
        messageCampaignId =
          typeof dataCampaignId === 'number'
            ? dataCampaignId
            : typeof dataCampaignId === 'string'
            ? Number.parseInt(dataCampaignId, 10)
            : undefined;
      }
    }
  }

  // If still no name found, keep original
  if (messageDisplayName == null || messageDisplayName === '') {
    return conditionData;
  }

  // Add message name and campaign_id to the event object if present, otherwise to the top level
  if (event != null) {
    const enrichedEvent: Record<string, unknown> = {
      ...event,
      message_name: messageDisplayName,
    };
    // Add campaign_id if we found it from the message
    if (messageCampaignId != null) {
      enrichedEvent.campaign_id = messageCampaignId;
    }
    return {
      ...conditionData,
      event: enrichedEvent,
    };
  }

  const enriched: Record<string, unknown> = {
    ...conditionData,
    message_name: messageDisplayName,
  };
  // Add campaign_id if we found it from the message
  if (messageCampaignId != null) {
    enriched.campaign_id = messageCampaignId;
  }
  return enriched;
}

/**
 * Enrich condition data with Newsletter name from newsletters list.
 * This is used to display the actual newsletter name instead of "Newsletter #1".
 */
export function enrichConditionWithNewsletterName(
  conditionData: Record<string, unknown>,
  newsletters?: Array<{ id: number; name: string }>,
): Record<string, unknown> {
  if (newsletters == null || newsletters.length === 0) {
    return conditionData;
  }

  // Check for newsletter_recipients condition
  if (conditionData.type !== 'newsletter_recipients' && !('newsletter_recipients' in conditionData)) {
    return conditionData;
  }

  let newsletterId: number | string | undefined;

  // Handle { newsletter_recipients: { id: ... } } format
  if (
    'newsletter_recipients' in conditionData &&
    typeof conditionData.newsletter_recipients === 'object' &&
    conditionData.newsletter_recipients != null
  ) {
    const nr = conditionData.newsletter_recipients as Record<string, unknown>;
    newsletterId = nr.id as number | string | undefined;
  } else {
    newsletterId = conditionData.newsletter_id as number | string | undefined;
  }

  if (newsletterId == null) {
    return conditionData;
  }

  // Look up newsletter name
  const newsletter = newsletters.find((n) => Number(n.id) === Number(newsletterId));
  if (newsletter == null) {
    return conditionData;
  }

  return {
    ...conditionData,
    newsletter_name: newsletter.name,
  };
}

/**
 * Enrich condition data with Segment names from a segments list.
 * This handles the segments_negatable format with multiple segment IDs.
 * Creates a segment_names map that the parser can use for name lookup.
 */
export function enrichConditionWithSegmentsList(
  conditionData: Record<string, unknown>,
  segments?: Array<{ id: number; name: string }>,
): Record<string, unknown> {
  if (segments == null || segments.length === 0) {
    return conditionData;
  }

  // Build a map of segment ID -> name for quick lookup
  // Normalize IDs to numbers to handle string/number type mismatches
  const segmentMap = new Map(segments.map((s) => [Number(s.id), s.name]));

  // Handle segment_id field
  const segmentId = conditionData.segment_id as number | undefined;
  if (segmentId != null && segmentId > 0) {
    const normalizedId = Number(segmentId);
    if (segmentMap.has(normalizedId)) {
      conditionData = {
        ...conditionData,
        segment_name: segmentMap.get(normalizedId),
      };
    }
  }

  // Handle segments_negatable format with segments array
  if (conditionData.type === 'segments_negatable' && Array.isArray(conditionData.segments)) {
    const segmentIds = conditionData.segments as (string | number)[];
    const segmentNames: Record<string, string> = {};

    for (const id of segmentIds) {
      const numId = typeof id === 'string' ? Number.parseInt(id, 10) : id;
      const normalizedId = Number(numId);
      const segmentName = segmentMap.get(normalizedId);
      if (segmentName != null) {
        segmentNames[String(numId)] = segmentName;
      }
    }

    if (Object.keys(segmentNames).length > 0) {
      return {
        ...conditionData,
        segment_names: segmentNames,
      };
    }
  }

  // Handle nested segment object
  if ('segment' in conditionData && typeof conditionData.segment === 'object' && conditionData.segment != null) {
    const seg = conditionData.segment as Record<string, unknown>;
    const nestedSegmentId = seg.id as number | undefined;
    if (nestedSegmentId != null && nestedSegmentId > 0) {
      const normalizedId = Number(nestedSegmentId);
      if (segmentMap.has(normalizedId)) {
        const segmentName = segmentMap.get(normalizedId);
        // Also add to segment_names map for parser lookup
        const segmentNames = (conditionData.segment_names as Record<string, string> | undefined) ?? {};
        return {
          ...conditionData,
          segment: {
            ...seg,
            name: segmentName,
          },
          segment_name: segmentName,
          segment_names: {
            ...segmentNames,
            [String(normalizedId)]: segmentName!,
          },
        };
      }
    }
  }

  return conditionData;
}

/**
 * Helper to find form_id value in a filter object or array of filters.
 */
function findFormIdInFilters(filters: Record<string, unknown> | Array<Record<string, unknown>>): string | undefined {
  // Handle array of filters
  if (Array.isArray(filters)) {
    for (const filter of filters) {
      const field = (filter.field ?? filter.attribute) as string | undefined;
      if (field === 'form_id') {
        return filter.value as string | undefined;
      }
    }
    return undefined;
  }

  // Handle filter object with and/or structure
  const andFilters = filters.and as Array<Record<string, unknown>> | undefined;
  if (andFilters != null) {
    const found = findFormIdInFilters(andFilters);
    if (found != null) return found;
  }

  const orFilters = filters.or as Array<Record<string, unknown>> | undefined;
  if (orFilters != null) {
    const found = findFormIdInFilters(orFilters);
    if (found != null) return found;
  }

  // Handle direct filter object
  const field = (filters.field ?? filters.attribute) as string | undefined;
  if (field === 'form_id') {
    return filters.value as string | undefined;
  }

  return undefined;
}

/**
 * Enrich condition data with form name from form ID mappings.
 * This maps form_id values to human-readable form names (from segment's linked_form_id -> name).
 * Handles and/or structures and direct filter objects.
 */
export function enrichConditionWithFormName(
  conditionData: Record<string, unknown>,
  formNames?: Record<string, string>,
): Record<string, unknown> {
  if (formNames == null || Object.keys(formNames).length === 0) {
    return conditionData;
  }

  // Check if we have an event object with filters containing form_id
  const event = conditionData.event as Record<string, unknown> | undefined;
  if (event?.filters != null) {
    const filters = event.filters as Record<string, unknown>;
    const formId = findFormIdInFilters(filters);

    if (formId != null && formNames[formId] != null) {
      return {
        ...conditionData,
        form_name: formNames[formId],
      };
    }
  }

  return conditionData;
}

/**
 * Enrich condition data with object type name from object type ID mappings.
 * This maps type_id/object_type_id values to human-readable object type names.
 *
 * @param conditionData - The condition data to enrich
 * @param objectTypes - Array of object types with id and name
 * @returns Enriched condition data with object_type_name added if a matching type_id is found
 *
 * Handles:
 * - { type: 3, relationship_filter: { type_id: 123 } } -> adds object_type_name
 * - { type: 4, related_object_attr_filter: { type_id: 123 } } -> adds object_type_name
 * - { type: 5, relationship_attr_filter: { type_id: 123 } } -> adds object_type_name
 * - { relationship: { type_id: 123 } } -> adds object_type_name
 * - { relationship_filter: { type_id: 123 } } -> adds object_type_name
 * - { relationship_attr_filter: { type_id: 123 } } -> adds object_type_name
 * - { related_object_attr_filter: { type_id: 123 } } -> adds object_type_name
 * - { type: 'related', object_type_id: 123 } -> adds object_type_name
 * - { type: 'relationship', type_id: 123 } -> adds object_type_name
 * - { and: [{ relationship: { type_id: 123 } }, ...] } -> adds object_type_name
 */
export function enrichConditionWithObjectTypeName(
  conditionData: Record<string, unknown>,
  objectTypes?: Array<{ id: number; name: string }>,
): Record<string, unknown> {
  if (objectTypes == null || objectTypes.length === 0) {
    return conditionData;
  }

  // Create a lookup map
  const objectTypeMap = new Map(objectTypes.map((ot) => [ot.id, ot.name]));

  // Check for { relationship_filter: { type_id } } format (backend condition format)
  if (
    'relationship_filter' in conditionData &&
    typeof conditionData.relationship_filter === 'object' &&
    conditionData.relationship_filter != null
  ) {
    const relFilter = conditionData.relationship_filter as Record<string, unknown>;
    const typeId = relFilter.type_id as number | undefined;
    if (typeId != null && objectTypeMap.has(typeId)) {
      return {
        ...conditionData,
        object_type_name: objectTypeMap.get(typeId),
      };
    }
  }

  // Check for { relationship_attr_filter: { type_id } } format (object attribute condition)
  if (
    'relationship_attr_filter' in conditionData &&
    typeof conditionData.relationship_attr_filter === 'object' &&
    conditionData.relationship_attr_filter != null
  ) {
    const attrFilter = conditionData.relationship_attr_filter as Record<string, unknown>;
    const typeId = attrFilter.type_id as number | undefined;
    if (typeId != null && objectTypeMap.has(typeId)) {
      return {
        ...conditionData,
        object_type_name: objectTypeMap.get(typeId),
      };
    }
  }

  // Check for { related_object_attr_filter: { type_id } } format (related object attribute condition)
  if (
    'related_object_attr_filter' in conditionData &&
    typeof conditionData.related_object_attr_filter === 'object' &&
    conditionData.related_object_attr_filter != null
  ) {
    const attrFilter = conditionData.related_object_attr_filter as Record<string, unknown>;
    const typeId = attrFilter.type_id as number | undefined;
    if (typeId != null && objectTypeMap.has(typeId)) {
      return {
        ...conditionData,
        object_type_name: objectTypeMap.get(typeId),
      };
    }
  }

  // Check for { relationship: { type_id } } format
  if (
    'relationship' in conditionData &&
    typeof conditionData.relationship === 'object' &&
    conditionData.relationship != null
  ) {
    const rel = conditionData.relationship as Record<string, unknown>;
    const typeId = rel.type_id as number | undefined;
    if (typeId != null && objectTypeMap.has(typeId)) {
      return {
        ...conditionData,
        object_type_name: objectTypeMap.get(typeId),
      };
    }
  }

  // Check for { and: [{ relationship: { type_id } }, ...] } format
  if ('and' in conditionData && Array.isArray(conditionData.and)) {
    const andArray = conditionData.and as Array<Record<string, unknown>>;
    const relationshipItem = andArray.find(
      (item) => 'relationship' in item && typeof item.relationship === 'object' && item.relationship != null,
    );
    if (relationshipItem != null) {
      const rel = relationshipItem.relationship as Record<string, unknown>;
      const typeId = rel.type_id as number | undefined;
      if (typeId != null && objectTypeMap.has(typeId)) {
        return {
          ...conditionData,
          object_type_name: objectTypeMap.get(typeId),
        };
      }
    }
  }

  // Check for type: 'related', 'relationship', 3, or 5 with object_type_id
  const conditionType = conditionData.type;
  if (conditionType === 'related' || conditionType === 'relationship' || conditionType === 3 || conditionType === 5) {
    const objectTypeId = (conditionData.object_type_id ?? conditionData.type_id ?? conditionData.typeId) as
      | number
      | undefined;
    if (objectTypeId != null && objectTypeMap.has(objectTypeId)) {
      return {
        ...conditionData,
        object_type_name: objectTypeMap.get(objectTypeId),
      };
    }
  }

  return conditionData;
}

// =============================================================================
// Empty Condition Checks
// =============================================================================

/**
 * Check if conditions are empty (null, empty object, or empty array).
 */
export function isEmptyConditions(
  conditions: Record<string, unknown> | Record<string, unknown>[] | null | undefined,
): boolean {
  return (
    conditions == null ||
    (typeof conditions === 'object' && !Array.isArray(conditions) && Object.keys(conditions).length === 0) ||
    (Array.isArray(conditions) && conditions.length === 0)
  );
}

// =============================================================================
// Reference Detection
// =============================================================================

/**
 * Check if conditions object is just a reference without actual condition details.
 * This happens when the audit log stores { segment_id: 0, condition_id: 20 }
 * instead of the actual condition tree.
 */
export function isJustReference(conditions: Record<string, unknown>): boolean {
  // Helper to check if a value has children
  const hasChildren = (value: unknown): boolean => {
    if (Array.isArray(value) && value.length > 0) return true;
    if (typeof value === 'object' && value != null) {
      const obj = value as Record<string, unknown>;
      return Array.isArray(obj.children) && obj.children.length > 0;
    }
    return false;
  };

  // If it has actual condition data (and/or with children), it's not just a reference
  if (hasChildren(conditions.and) || hasChildren(conditions.or)) {
    return false;
  }

  // If it only has reference IDs, it's just a reference
  const hasConditionId =
    'condition_id' in conditions && typeof conditions.condition_id === 'number' && conditions.condition_id > 0;
  const hasSegmentId =
    'segment_id' in conditions && typeof conditions.segment_id === 'number' && conditions.segment_id > 0;

  return hasConditionId || hasSegmentId;
}

// =============================================================================
// Condition Data Extraction
// =============================================================================

/**
 * Extract condition data from raw audit log data.
 * Handles various formats: attribute_filter, nested conditions, and/or arrays, etc.
 */
export function extractConditionData(data: Record<string, unknown>): Record<string, unknown> | null {
  // Check for nested conditions field first
  if ('conditions' in data && data.conditions != null && typeof data.conditions === 'object') {
    return data.conditions as Record<string, unknown>;
  }

  // Check for attribute_filter (common format for saved conditions)
  // Handle both attrFilter.field and attrFilter.attribute to match parseAttributeCondition behavior
  if ('attribute_filter' in data && data.attribute_filter != null && typeof data.attribute_filter === 'object') {
    const attrFilter = data.attribute_filter as Record<string, unknown>;
    const fieldName = (attrFilter.attribute ?? attrFilter.field) as string | undefined;

    // Determine type based on data.type:
    // type: 0 = attribute, type: 1 = event, type: 2 = device_attribute
    const conditionType = data.type === 2 ? 'device_attribute' : data.type === 1 ? 'event_attribute' : 'attribute';

    return {
      attribute: fieldName,
      field: fieldName,
      operator: attrFilter.operator,
      value: attrFilter.value,
      value2: attrFilter.value2,
      inverse: attrFilter.inverse,
      type: conditionType,
    };
  }

  // Check if data itself has and/or arrays
  if ('and' in data && Array.isArray(data.and) && data.and.length > 0) {
    return { and: data.and };
  }
  if ('or' in data && Array.isArray(data.or) && data.or.length > 0) {
    return { or: data.or };
  }

  // For all other cases, strip only true metadata fields but preserve condition data fields
  // Metadata fields: id, timestamps, description, processing state, etc.
  // Condition data fields to PRESERVE: type, name, operator, value, inverse, event_type_id, etc.
  const {
    id: _id,
    created_at: _created_at,
    updated_at: _updated_at,
    description: _description,
    processing: _processing,
    finished: _finished,
    segments_watching: _segments_watching,
    default_match: _default_match,
    // Only strip segment_id/condition_id if they're 0 or not meaningful
    // Keep them if they might be part of the actual condition reference
    ...conditionData
  } = data;

  // Filter out segment_id and condition_id only if they're 0 (placeholder values)
  const result = { ...conditionData };
  if ('segment_id' in result && result.segment_id === 0) {
    delete result.segment_id;
  }
  if ('condition_id' in result && result.condition_id === 0) {
    delete result.condition_id;
  }

  if (Object.keys(result).length > 0) {
    return result;
  }

  return null;
}
